// Display formatting for report values. One resolver serves tables, charts, KPIs, funnels and
// the builder, so the preview and the viewer always agree. The server's AnalyticsValueFormatter
// applies the same order for exports:
//   1. the column's own setting (display.columns[alias])
//   2. the report-wide setting (display.numberFormat, currency, decimals, abbreviate)
//   3. the default for the column's field kind
// Counts never take a report-wide currency, percent or decimals. Calendar days are never shifted
// between time zones; timestamps show in the reporting time zone.

import type { ColumnFormat, QueryDisplay, QueryResultColumn } from './types';

export type NumberStyle = 'number' | 'currency' | 'percent' | 'ratio';

export interface ResolvedFormat {
    numberFormat: NumberStyle;
    currency: string | null;
    decimals: number | null;
    abbreviate: boolean;
    dateStyle: 'short' | 'medium' | 'long' | null;
    showTime: boolean;
    align: 'start' | 'center' | 'end';
    wrap: boolean;
}

export interface FormatContext {
    /** Whether report-wide number settings apply: measures, and every column of a record list. */
    valueColumn?: boolean;
    /** The row being shown, so money columns can read their record's currency. */
    row?: Record<string, unknown> | null;
    /** Day and week buckets on an axis that crosses a year boundary show the year, as exports always do. */
    withYear?: boolean;
}

/** Where a column's format comes from, for the Format panel. */
export type FormatSource = 'automatic' | 'inherited' | 'overridden';

const CURRENCY_CODE = /^[A-Z]{3}$/;
const EXACT_NUMBER = /^-?\d+(\.\d+)?$/;
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.0+)?)?$/;

export function isValueColumn(column: QueryResultColumn, reportType?: string | null): boolean {
    return column.kind === 'measure' || reportType === 'recordList';
}

export function isCount(column: QueryResultColumn): boolean {
    return column.unit === 'count' || (column.kind === 'measure' && (column.aggregate === 'count' || column.aggregate === 'countDistinct'));
}

export function columnFormat(display: QueryDisplay | null | undefined, column: QueryResultColumn): ColumnFormat | null {
    return display?.columns?.[column.alias] ?? null;
}

export function formatSource(column: QueryResultColumn, display: QueryDisplay | null | undefined, valueColumn: boolean): FormatSource {
    const own = columnFormat(display, column);
    if (own && Object.values(own).some(v => v != null)) return 'overridden';
    if (valueColumn && !isCount(column) && display && (display.numberFormat && display.numberFormat !== 'auto' || display.decimals != null || display.currency)) return 'inherited';
    if (valueColumn && display?.abbreviate) return 'inherited';
    return 'automatic';
}

function kindFormat(column: QueryResultColumn): NumberStyle {
    if (column.unit === 'ratio') return 'ratio';
    if (column.unit === 'percent' || column.aggregate === 'percentOfTotal' || column.fieldKind === 'percent') return 'percent';
    if (!isCount(column) && (column.fieldKind === 'currency' || column.unit === 'currency')) return 'currency';
    return 'number';
}

export function resolveFormat(column: QueryResultColumn, display?: QueryDisplay | null, context: FormatContext = {}): ResolvedFormat {
    const valueColumn = context.valueColumn ?? column.kind === 'measure';
    const own = columnFormat(display, column);
    const report = valueColumn && !isCount(column) ? display ?? null : null;
    let numberFormat: string | null | undefined = own?.numberFormat ?? report?.numberFormat;
    if (numberFormat == null || numberFormat === 'auto') numberFormat = kindFormat(column);
    const rowCurrency = column.currencyAlias ? context.row?.[column.currencyAlias] : null;
    const currency = own?.currency ?? (typeof rowCurrency === 'string' && CURRENCY_CODE.test(rowCurrency) ? rowCurrency : null) ?? report?.currency ?? null;
    const decimals = own?.decimals ?? report?.decimals
        ?? (numberFormat === 'currency' ? 2 : numberFormat === 'percent' || numberFormat === 'ratio' ? 1 : null);
    return {
        numberFormat: numberFormat as NumberStyle,
        currency,
        decimals,
        abbreviate: own?.abbreviate ?? (valueColumn ? display?.abbreviate ?? null : null) ?? false,
        dateStyle: own?.dateStyle ?? null,
        showTime: own?.showTime ?? (column.fieldKind === 'datetime' && !column.timeGrain),
        align: own?.align ?? (column.dataType === 'number' ? 'end' : 'start'),
        wrap: own?.wrap ?? false,
    };
}

export function formatValue(value: unknown, column: QueryResultColumn, display?: QueryDisplay | null, context: FormatContext = {}): string {
    if (value == null) return '';
    if (column.dataType === 'number') return formatNumber(value, resolveFormat(column, display, context));
    if (column.dataType === 'date') return formatDate(value, column, resolveFormat(column, display, context), context.withYear === true);
    if (column.dataType === 'boolean' || typeof value === 'boolean') return value === true || value === 'true' ? 'Yes' : value === false || value === 'false' ? 'No' : String(value);
    return String(value);
}

// Formatters are built once per set of options: building one per cell made a 10,000-point chart take a third of
// a second before drawing (ANX-0511). The browser's locale never changes while a page is open.
const NUMBER_FORMATS = new Map<string, Intl.NumberFormat>();
const DATE_FORMATS = new Map<string, Intl.DateTimeFormat>();
function numberFormatter(options: Intl.NumberFormatOptions): Intl.NumberFormat {
    const key = JSON.stringify(options);
    let formatter = NUMBER_FORMATS.get(key);
    if (!formatter) { formatter = new Intl.NumberFormat(undefined, options); NUMBER_FORMATS.set(key, formatter); }
    return formatter;
}
/** The same text as `date.toLocaleString(undefined, options)` for options that include date fields or none. */
function dateText(date: Date, options: Intl.DateTimeFormatOptions): string {
    const key = JSON.stringify(options);
    let formatter = DATE_FORMATS.get(key);
    if (!formatter) { formatter = new Intl.DateTimeFormat(undefined, options); DATE_FORMATS.set(key, formatter); }
    return formatter.format(date);
}

function formatNumber(value: unknown, format: ResolvedFormat): string {
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value);
    // Exact decimal strings keep every digit; Intl formats numeric strings without converting to a double.
    const exact = typeof value === 'string' && EXACT_NUMBER.test(value);
    let input: number | string = exact ? value as string : n;
    if (format.numberFormat === 'ratio') input = n * 100;
    // Compact notation keeps its own rounding (1.2K, 12K) unless the author chose decimals.
    const options: Intl.NumberFormatOptions = format.decimals != null
        ? { minimumFractionDigits: format.decimals, maximumFractionDigits: format.decimals }
        : format.abbreviate ? {} : { maximumFractionDigits: exact ? 20 : 2 };
    if (format.abbreviate) options.notation = 'compact';
    if (format.numberFormat === 'currency' && format.currency) Object.assign(options, { style: 'currency', currency: format.currency });
    let text: string;
    try { text = numberFormatter(options).format(input as number); }
    catch { text = n.toLocaleString(undefined, { maximumFractionDigits: 2 }); }
    return format.numberFormat === 'percent' || format.numberFormat === 'ratio' ? `${text}%` : text;
}

function dayOptions(style: ResolvedFormat['dateStyle']): Intl.DateTimeFormatOptions {
    if (style === 'medium') return { year: 'numeric', month: 'short', day: 'numeric' };
    if (style === 'long') return { year: 'numeric', month: 'long', day: 'numeric' };
    return { year: 'numeric', month: 'numeric', day: 'numeric' };
}

/** A fiscal year that starts in January is the calendar year; any other start spans two calendar years. */
export function fiscalYearLabel(startYear: number, fiscalStartMonth: number): string {
    return fiscalStartMonth <= 1 ? String(startYear) : `FY${startYear}/${String((startYear + 1) % 100).padStart(2, '0')}`;
}

function formatDate(value: unknown, column: QueryResultColumn, format: ResolvedFormat, withYear = false): string {
    const raw = String(value);
    if (column.timeGrain) {
        // SQL buckets are local calendar labels, not instants in the browser timezone.
        const d = new Date(/(Z|[+-]\d{2}:\d{2})$/.test(raw) ? raw : /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T00:00:00Z` : `${raw}Z`);
        if (Number.isNaN(d.getTime())) return raw;
        const timeZone = 'UTC';
        const fiscal = column.fiscalYearStartMonth ?? 1;
        if (column.timeGrain === 'year') return fiscalYearLabel(d.getUTCFullYear(), fiscal);
        if (column.timeGrain === 'month') return dateText(d, { year: 'numeric', month: format.dateStyle === 'long' ? 'long' : 'short', timeZone });
        if (column.timeGrain === 'quarter') {
            const start = fiscal - 1;
            const quarter = Math.floor(((d.getUTCMonth() - start + 12) % 12) / 3) + 1;
            const year = d.getUTCFullYear() - (d.getUTCMonth() < start ? 1 : 0);
            return `Q${quarter} ${fiscalYearLabel(year, fiscal)}`;
        }
        if (column.timeGrain === 'week' || column.timeGrain === 'day') {
            // The author's date style wins; otherwise the year appears once the axis crosses a year boundary.
            const options: Intl.DateTimeFormatOptions = format.dateStyle ? { ...dayOptions(format.dateStyle), timeZone }
                : withYear ? { year: 'numeric', month: 'short', day: 'numeric', timeZone } : { month: 'short', day: 'numeric', timeZone };
            return dateText(d, options);
        }
        return dateText(d, { timeZone });
    }
    const day = DATE_ONLY.exec(raw);
    if (day && (column.fieldKind === 'date' || (!column.fieldKind && !column.timeZone))) {
        // A calendar day is the same day everywhere.
        const d = new Date(Date.UTC(Number(day[1]), Number(day[2]) - 1, Number(day[3])));
        return dateText(d, { ...dayOptions(format.dateStyle), timeZone: 'UTC' });
    }
    const d = new Date(/(Z|[+-]\d{2}:?\d{2})$/.test(raw) || !/T\d/.test(raw) ? raw : `${raw}Z`);
    if (Number.isNaN(d.getTime())) return raw;
    const timeZone = column.timeZone || 'UTC';
    const options: Intl.DateTimeFormatOptions = { ...dayOptions(format.dateStyle), timeZone };
    if (format.showTime) Object.assign(options, { hour: '2-digit', minute: '2-digit' });
    try { return dateText(d, options); }
    catch { return dateText(d, { ...options, timeZone: 'UTC' }); }
}

/** The one label for a missing group value everywhere: tables, pivots, charts, legends and drill choices. */
export const NO_VALUE_LABEL = '(No value)';
export const EMPTY_TEXT_LABEL = '(Empty)';

/** Null and empty text are real, distinct groups; never confuse either with zero or with each other. */
export function missingLabel(value: unknown): string | null {
    return value == null ? NO_VALUE_LABEL : value === '' ? EMPTY_TEXT_LABEL : null;
}

/** Table cell text, distinguishing a missing value from empty text. */
export function displayCell(value: unknown, column: QueryResultColumn, display?: QueryDisplay | null, context: FormatContext = {}): string {
    return missingLabel(value) ?? formatValue(value, column, display, context);
}

/** Additive aggregates can be summed across groups; averages and distinct counts cannot. */
export function isAdditive(column: QueryResultColumn): boolean {
    const agg = column.aggregate;
    return column.kind === 'measure' && (agg === 'count' || agg === 'sum' || agg === 'percentOfTotal');
}

/** Aggregates whose total across groups can be computed from the group values (sum, or min/max of mins/maxes). */
export function combineMeasure(column: QueryResultColumn, values: unknown[]): number | null {
    const numbers = values.map(v => v == null || v === '' ? null : Number(v)).filter((n): n is number => n != null && Number.isFinite(n));
    switch (column.aggregate) {
        case 'count': case 'sum': case 'percentOfTotal': return numbers.reduce((sum, n) => sum + n, 0);
        case 'min': return numbers.length ? Math.min(...numbers) : null;
        case 'max': return numbers.length ? Math.max(...numbers) : null;
        default: return null;
    }
}

/** Whether combineMeasure can total this measure on the client. */
export function isCombinable(column: QueryResultColumn): boolean {
    return column.kind === 'measure' && ['count', 'sum', 'percentOfTotal', 'min', 'max'].includes(column.aggregate ?? '');
}

/** Escape text for HTML markup and attribute values (also Apex labels, which are written into the SVG as HTML). */
export function escapeHtml(s: string): string {
    return s.replace(/[&<>"']/g, (ch) => ch === '&' ? '&amp;' : ch === '<' ? '&lt;' : ch === '>' ? '&gt;' : ch === '"' ? '&quot;' : '&#39;');
}
